import { useEffect, useRef } from "react";

interface ObfuscatedEmailProps {
  className?: string;
  /** Text shown before JavaScript decodes the address. */
  fallback?: string;
}

/**
 * Obfuscated email link.
 *
 * The address is stored base64-encoded in a data attribute, so neither the
 * user, the "@", nor the domain appear as readable text in the raw HTML
 * source. After mount the value is decoded in the browser and used to set
 * both the visible label and the mailto: href, so real visitors get a normal
 * working email link while non-JS scrapers find nothing harvestable.
 */
// base64("hello" + "@" + "wevang.com")
const ENCODED = "aGVsbG9Ad2V2YW5nLmNvbQ==";

function decode(value: string): string {
  if (typeof atob === "function") return atob(value);
  return "";
}

export function ObfuscatedEmail({ className = "", fallback = "Contact us" }: ObfuscatedEmailProps) {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const link = linkRef.current;
    if (!link) return;
    const address = decode(link.dataset["e"] ?? ENCODED);
    if (!address) return;
    link.setAttribute("href", ["mail", "to:", address].join(""));
    link.textContent = address;
    link.setAttribute("aria-label", "Email us");
  }, []);

  return (
    <a ref={linkRef} className={className} aria-label="Email us" data-e={ENCODED}>
      {fallback}
    </a>
  );
}
